/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationMenuItem } from '../../models/navigation-menu-item';

@Component({
  selector: 'fc-edit-mode-panel',
  templateUrl: './edit-mode-panel.component.html',
  styleUrls: ['./edit-mode-panel.component.scss'],
})
export class EditModePanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  navItems: NavigationMenuItem[] = [];
  originNavItems: NavigationMenuItem[] = [];
  bookmarksItem: NavigationMenuItem;
  private _disabledItems: string[] = [];
  private menuItemsArray = [];

  @Output() adjustSidebarWidth = new EventEmitter<void>();
  @Output() onEditSave = new EventEmitter();
  @Output() onEditCancel = new EventEmitter();

  constructor(
    public sidebarService: SidebarService,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.adjustSidebarWidth.emit();

    this.sidebarService.navItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.navItems = cloneDeep(items);
        this.originNavItems = cloneDeep(items);
      });

    this.sidebarService.menuItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.menuItemsArray = this.menuItemsArray.filter(
          (item) => item.id !== items.id
        );
        this.menuItemsArray.push(items);
      });

    this.sidebarService.bookmarksItem$.pipe(takeUntil(this.destroy$)).subscribe((bookmarks) => {
      this.bookmarksItem = cloneDeep(bookmarks);
    });

    this.sidebarService.deletedBookmark
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.bookmarksItem.children = this.bookmarksItem.children?.filter(
          (bookmark) => bookmark.id !== id
        );
        this.ref.detectChanges();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.sidebarService.updateNavItems(this.originNavItems);
  }

  save() {
    this.copyManuItemsToNavItems();
    this._prepareDisabledItems();
    this.sidebarService.updateNavItems(this.navItems);
    this.sidebarService.updateBooksmarksItem(this.bookmarksItem);
    this.sidebarService.setEditState(false);
    this.onEditSave.emit();

    this.sidebarService
      .disableMenus(this._disabledItems)
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.menuItemsArray = [];
  }

  copyManuItemsToNavItems() {
    this.menuItemsArray.forEach((item) => {
      this.navItems = this.navItems.map((navItem) => {
        if (this.findParentWithId(item, navItem.id)) {
          if (item.children.length > 0) {
            const idMap = item.children.reduce((map, obj) => {
              map[obj.id] = obj;
              return map;
            }, {});

            const resultArray: any = navItem.children.map((obj) =>
              idMap[obj.id] ? idMap[obj.id] : obj
            );
            navItem.children = cloneDeep(resultArray);
            return navItem;
          }
          return item;
        }
        return navItem;
      });
    });
  }

  findParentWithId(obj, id) {
    if (obj.id === id) {
      return obj;
    }
    if (!obj.parent) {
      return undefined;
    }
    return this.findParentWithId(obj.parent, id);
  }

  revert() {
    this.originNavItems.map((obj, i) => {
      obj.isExpanded = this.navItems[i].isExpanded;
    });
    this.sidebarService.updateNavItems(this.originNavItems);
    this.sidebarService.updateBooksmarksItem(this.bookmarksItem);
    this.sidebarService.setEditState(false);
    this.onEditCancel.emit();
  }

  private _prepareDisabledItems() {
    this._disabledItems = [];
    this.navItems.forEach((item) => {
      this._checkDisabled(item);
    });
  }

  private _checkDisabled(item: NavigationMenuItem) {
    if (!item.visible) {
      this._disabledItems.push(item.id);
    } else {
      if (item.children && item.children.length > 0) {
        item.children.forEach((child) => {
          this._checkDisabled(child);
        });
      }
    }
  }
}
